import React, { useRef, useState } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import config from '../../../config'
import { normalizeUrl } from '../../Elements/UI/fileLink'
import {
  buildPaymentPlan,
  saveReceipt,
  formatINR,
  formatDate,
  daysUntil
} from '../../../features/payments/dummyPayments'

const statusMeta = {
  paid: { label: 'Paid', chip: 'bl-chip--success', icon: 'checkmark-circle-outline' },
  review: { label: 'Receipt under review', chip: 'bl-chip--info', icon: 'time-outline' },
  overdue: { label: 'Overdue', chip: 'bl-chip--danger', icon: 'alert-circle-outline' },
  due: { label: 'Due soon', chip: 'bl-chip--warning', icon: 'calendar-outline' },
  upcoming: { label: 'Upcoming', chip: 'bl-chip--neutral', icon: 'calendar-clear-outline' }
}

// Receipt upload for one instalment — uses the same /upload/image API as documents
const ReceiptUpload = ({ flatId, instalment, onUploaded, canUpload }) => {
  const inputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const inputId = `receipt-${flatId}-${instalment.id}`

  const handleFile = async e => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File is too large (max 10 MB)')
      e.target.value = ''
      return
    }

    const formData = new FormData()
    formData.append('upload', file)

    setUploading(true)
    try {
      const res = await axios.post(`${config.apiUrl}/upload/image`, formData, {
        headers: { Authorization: localStorage.getItem('token') }
      })
      const url = res.data?.data
      if (!url) throw new Error('No file URL returned')

      saveReceipt(flatId, instalment.id, {
        url,
        name: file.name,
        uploadedOn: new Date().toISOString()
      })
      toast.success('Receipt uploaded. It will be verified by the admin.')
      onUploaded()
    } catch (err) {
      console.error('Receipt upload failed', err)
      toast.error('Receipt upload failed. Please try again.')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  if (instalment.receipt) {
    return (
      <a
        href={normalizeUrl(instalment.receipt.url)}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-secondary btn-sm"
        aria-label={`View receipt for ${instalment.label} (opens in a new tab)`}
      >
        <ion-icon name="receipt-outline" aria-hidden="true"></ion-icon>
        View receipt
      </a>
    )
  }

  if (!canUpload || instalment.status === 'paid' || instalment.status === 'upcoming') return null

  return (
    <>
      <label htmlFor={inputId} className="visually-hidden">
        Upload payment receipt for {instalment.label}
      </label>
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="d-none"
        onChange={handleFile}
      />
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
      >
        {uploading
          ? <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
          : <ion-icon name="cloud-upload-outline" aria-hidden="true"></ion-icon>}
        {uploading ? 'Uploading…' : 'Upload receipt'}
      </button>
    </>
  )
}

// Full payment breakdown for one flat
const PaymentPlan = ({ flat, canUpload = true, onReceiptUploaded }) => {
  // bump to re-read receipts from storage after an upload
  const [version, setVersion] = useState(0)
  const plan = buildPaymentPlan(flat)

  if (!plan) return null

  const progress = plan.total ? Math.round((plan.paid / plan.total) * 100) : 0

  return (
    <div className="bl-pay" data-version={version}>
      {/* Summary */}
      <div className="card bl-pay__summary">
        <div className="bl-pay__stats">
          <div>
            <span className="bl-pay__label">Extra area</span>
            <span className="bl-pay__value">{plan.extraAreaSqft} sq.ft</span>
          </div>
          <div>
            <span className="bl-pay__label">Rate</span>
            <span className="bl-pay__value">{formatINR(plan.rate)}<small>/sq.ft</small></span>
          </div>
          <div>
            <span className="bl-pay__label">Extra flat amount</span>
            <span className="bl-pay__value bl-pay__value--strong">{formatINR(plan.total)}</span>
          </div>
        </div>

        <div className="bl-pay__progress">
          <div className="d-flex justify-content-between small mb-1">
            <span>Paid <strong>{formatINR(plan.paid)}</strong></span>
            <span>Balance <strong>{formatINR(plan.balance)}</strong></span>
          </div>
          <div
            className="bl-pay__bar"
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${progress}% of extra flat amount paid`}
          >
            <span style={{ width: `${progress}%` }}></span>
          </div>
        </div>
      </div>

      {/* Instalments */}
      <h3 className="section-title px-0">Payment schedule</h3>
      <ol className="bl-pay__list">
        {plan.instalments.map(inst => {
          const meta = statusMeta[inst.status]
          const days = daysUntil(inst.dueDate)
          return (
            <li key={inst.id} className={`card bl-pay__item is-${inst.status}`}>
              <div className="bl-pay__item-main">
                <div className="bl-pay__item-title">{inst.label}</div>
                <div className="bl-pay__item-meta">
                  Due {formatDate(inst.dueDate)}
                  {inst.status === 'overdue' && <span className="text-danger"> · {Math.abs(days)} days overdue</span>}
                  {inst.status === 'due' && <span> · in {days} {days === 1 ? 'day' : 'days'}</span>}
                </div>
                <span className={`bl-chip ${meta.chip} mt-2`}>
                  <ion-icon name={meta.icon} aria-hidden="true"></ion-icon>
                  {meta.label}
                </span>
              </div>
              <div className="bl-pay__item-side">
                <div className="bl-pay__amount">{formatINR(inst.amount)}</div>
                <ReceiptUpload
                  flatId={plan.flatId}
                  instalment={inst}
                  canUpload={canUpload}
                  onUploaded={() => {
                    setVersion(v => v + 1)
                    if (onReceiptUploaded) onReceiptUploaded()
                  }}
                />
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export default PaymentPlan
